import { LiveKitClient } from "@share/live-kit/live-kit-classes/LiveKitClient";
import {
    LocalParticipant,
    LocalTrackPublication,
    RemoteAudioTrack,
    RemoteParticipant,
    RemoteTrack,
    RemoteTrackPublication,
    RemoteVideoTrack,
    RoomEvent,
    RoomState,
    Track
} from "livekit-client";
import { LiveKitParticipantsEventsHandler } from "@share/live-kit/live-kit-classes/LiveKitParticipantsEventsHandler";
import { liveKitIsTrackSharing } from "@share/live-kit/helpers/LiveKitIsTrackSharing";
import { ReconnectingController } from "@share/live-kit/controllers/ReconectingController";
import Source = Track.Source;

export class LiveKitRoomEventsHandler {
    isAutoPlayError = false;

    constructor(
        private readonly client: LiveKitClient,
        private readonly participantsEventsHandler: LiveKitParticipantsEventsHandler,
        private readonly reconnectingController: ReconnectingController
    ) {
        this.off();
        this.subscribe();
    }

    subscribe() {
        this.client.room.on(RoomEvent.TrackSubscribed, this.onTrackSubscribed.bind(this));
        this.client.room.on(RoomEvent.TrackUnsubscribed, this.onTrackUnsubscribed.bind(this));
        this.client.room.on(RoomEvent.LocalTrackPublished, this.onLocalTrackPublished.bind(this));
        this.client.room.on(RoomEvent.LocalTrackUnpublished, this.onLocalTrackUnpublished.bind(this));

        this.client.room.on(RoomEvent.ParticipantConnected, this.participantsEventsHandler.participantConnected.bind(this.participantsEventsHandler));
        this.client.room.on(RoomEvent.ParticipantDisconnected, this.participantsEventsHandler.participantDisconnected.bind(this.participantsEventsHandler));

        this.client.room.on(RoomEvent.StateChanged, (state) => {
            if (state != RoomState.Disconnected) {
                return;
            }

            this.reconnectingController.stopReconnecting(state);
        })

        this.client.room.on(RoomEvent.Reconnecting, () => {
            this.reconnectingController.startReconnecting();
        })

        this.client.room.on(RoomEvent.Reconnected, () => {
            this.reconnectingController.stopReconnecting(RoomState.Connected);
        })

        //@todo a dedicated handler
        this.client.room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
            console.log('audio playback changed', this.client.room.canPlaybackAudio);
            if (!this.client.room.canPlaybackAudio) {
                this.isAutoPlayError = true;
            }
        });
    }

    off() {
        this.client.room.removeAllListeners();
    }

    onTrackSubscribed(
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant
    ) {
        console.log('track subscribed', track, publication, participant);

        this.handleTracks(publication, participant);
    }

    onTrackUnsubscribed(
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant
    ) {
        console.log('track unsubscribed', track, publication, participant);

        this.handleTracks(publication, participant);
    }

    onLocalTrackPublished(publication: LocalTrackPublication) {
        console.log('local track published', this.client.room.localParticipant);

        this.handleTracks(publication, this.client.room.localParticipant);
    }

    onLocalTrackUnpublished(publication: LocalTrackPublication) {
        console.log('local track unpublished', this.client.room.localParticipant, publication.source);
        this.onLocalTrackPublished(publication);
    }

    handleTracks(publication: LocalTrackPublication | RemoteTrackPublication, participant: LocalParticipant | RemoteParticipant) {
        const isScreenSharing = liveKitIsTrackSharing(publication);

        if (isScreenSharing && !participant.isScreenShareEnabled) {
            console.log('removing', isScreenSharing, participant.identity);
            this.client.tracksManager.remove(participant.identity + '_sharing');

            return;
        }

        const videoTrack = participant.getTrack(isScreenSharing ? Source.ScreenShare : Source.Camera)?.track;
        const audioTrack = participant.getTrack(isScreenSharing ? Source.ScreenShareAudio : Source.Microphone)?.track;

        this.client.tracksManager.update({
            //@todo make sharing id helper
            uid: isScreenSharing ? participant.identity + '_sharing' : participant.identity,
            video: videoTrack as RemoteVideoTrack,
            audio: audioTrack as RemoteAudioTrack,
            isLocal: publication instanceof LocalTrackPublication,
            isScreenShare: isScreenSharing
        });
    }

    async removeAutoplayError() {
        if(this.client) {
            try {
                await this.client.room.startAudio();
                Object.values(this.client.tracksManager.tracks).forEach(item => {
                    if (item.video && item.video.attachedElements.length > 0) {
                        item.video.attachedElements[0].play();
                    }
                })
            }
            catch (e) {
                console.log(e);
            }
            this.isAutoPlayError = false;
        }
    }
}
